// ===== weatherPoints.ts — pure C1 weather-point layer logic =====
//
// DOM-free helpers for the weather-points map layer (tranche C1), built on
// the B1 shared forecast foundation (forecastCells.ts + windField.ts):
//
//   - WMO weather-code → icon/label/severity mapping (standard codes 0-99,
//     Open-Meteo wording). Severe codes (65/67/75/82/86/95/96/99) rank
//     above every non-severe code so a severe condition can never be
//     dropped by grouping.
//   - planWeatherMarkers(): zoom-dependent split/combine of the REAL cells
//     into markers — at close zoom every real cell is its own marker
//     (stride 1); at wide zoom each marker combines the stride×stride
//     block of real cells and sits on the block's top-left real cell.
//   - combineHours(): combined marker values — severe condition wins,
//     temperature averages, humidity averages, highest rain chance wins,
//     highest rain amount wins (never understates rain).
//   - isRainLikely(): the marker rain-chance line shows only when rain is
//     actually likely (>= RAIN_LIKELY_MIN).
//   - hourLabel()/fmtLatLon(): the details-card wording for the selected
//     forecast hour and the source cell.
//
// The DOM renderer lives in weatherPointsRenderer.ts; this module stays
// pure so every grouping/averaging/priority rule is unit-testable.

import type { ForecastCell } from './forecastCells';
import { zoomCellStride } from './forecastCells';
import type { WindPointHour } from './windField';

// ── WMO weather-code mapping (standard Open-Meteo codes) ──

export type WeatherIconKey =
  | 'sun'
  | 'partly'
  | 'cloud'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'heavy-rain'
  | 'snow'
  | 'freezing'
  | 'thunder';

export interface WeatherCodeMeta {
  icon: WeatherIconKey;
  label: string;
  severe: boolean;
}

/** Codes that must never disappear during grouping (C1-3/C1-4). */
export const SEVERE_WEATHER_CODES = new Set([65, 67, 75, 82, 86, 95, 96, 99]);

const WEATHER_CODE_META: Record<number, { icon: WeatherIconKey; label: string }> = {
  0: { icon: 'sun', label: 'Clear sky' },
  1: { icon: 'sun', label: 'Mainly clear' },
  2: { icon: 'partly', label: 'Partly cloudy' },
  3: { icon: 'cloud', label: 'Overcast' },
  45: { icon: 'fog', label: 'Fog' },
  48: { icon: 'fog', label: 'Depositing rime fog' },
  51: { icon: 'drizzle', label: 'Light drizzle' },
  53: { icon: 'drizzle', label: 'Drizzle' },
  55: { icon: 'drizzle', label: 'Dense drizzle' },
  56: { icon: 'freezing', label: 'Freezing drizzle' },
  57: { icon: 'freezing', label: 'Freezing drizzle' },
  61: { icon: 'rain', label: 'Light rain' },
  63: { icon: 'rain', label: 'Rain' },
  65: { icon: 'heavy-rain', label: 'Heavy rain' },
  66: { icon: 'freezing', label: 'Freezing rain' },
  67: { icon: 'freezing', label: 'Heavy freezing rain' },
  71: { icon: 'snow', label: 'Light snow' },
  73: { icon: 'snow', label: 'Snow' },
  75: { icon: 'snow', label: 'Heavy snow' },
  77: { icon: 'snow', label: 'Snow grains' },
  80: { icon: 'rain', label: 'Light showers' },
  81: { icon: 'rain', label: 'Showers' },
  82: { icon: 'heavy-rain', label: 'Violent showers' },
  85: { icon: 'snow', label: 'Snow showers' },
  86: { icon: 'snow', label: 'Heavy snow showers' },
  95: { icon: 'thunder', label: 'Thunderstorm' },
  96: { icon: 'thunder', label: 'Thunderstorm with hail' },
  99: { icon: 'thunder', label: 'Severe thunderstorm with hail' },
};

/**
 * Worst-first rank for combining: every severe code outranks every
 * non-severe code (so severe always wins a group), and within a tier the
 * ordering is severity-first (thunderstorms worst, then heavy precip,
 * then plain rain/snow, fog, cloud, clear). Unknown codes rank 0.
 */
const WMO_RANK: Record<number, number> = {
  0: 1,
  1: 2,
  2: 3,
  3: 4,
  45: 5,
  48: 5,
  51: 6,
  53: 7,
  55: 8,
  56: 9,
  57: 9,
  61: 10,
  63: 11,
  66: 12,
  71: 13,
  73: 14,
  77: 13,
  80: 10,
  81: 11,
  85: 14,
  // Severe tier (all above every non-severe code).
  65: 60,
  67: 61,
  75: 62,
  82: 63,
  86: 64,
  95: 65,
  96: 66,
  99: 67,
};

/** Fallback for codes outside the standard table (unknown codes). */
const FALLBACK_META: WeatherCodeMeta = { icon: 'cloud', label: 'Forecast', severe: false };

export function weatherCodeMeta(code: number): WeatherCodeMeta {
  const meta = WEATHER_CODE_META[code];
  if (!meta) return FALLBACK_META;
  return { ...meta, severe: SEVERE_WEATHER_CODES.has(code) };
}

export function weatherCodeToIcon(code: number): WeatherIconKey {
  return weatherCodeMeta(code).icon;
}

export function isSevereWeatherCode(code: number): boolean {
  return SEVERE_WEATHER_CODES.has(code);
}

/** Worst-first ordering used by combining (see WMO_RANK). */
export function severityRank(code: number): number {
  return WMO_RANK[code] ?? 0;
}

/** Pick the worst code from a set (severe wins; ties keep first). */
export function pickWorstCode(codes: Array<number | null>): number | null {
  let best: number | null = null;
  let bestRank = -1;
  for (const c of codes) {
    if (c == null) continue;
    const r = severityRank(c);
    if (r > bestRank) {
      bestRank = r;
      best = c;
    }
  }
  return best;
}

// ── Rain-likely rule (C1-2: rain chance only when rain is likely) ──

/** Precipitation probability at/above which the marker shows its rain line. */
export const RAIN_LIKELY_MIN = 40;

export function isRainLikely(values: Pick<WeatherHourValues, 'rainChance'>): boolean {
  return values.rainChance != null && values.rainChance >= RAIN_LIKELY_MIN;
}

// ── Zoom split/combine (C1-4) ──

export interface WeatherMarkerGroup {
  /** Real cell the marker sits on (block top-left; a real source cell). */
  anchor: ForecastCell;
  /** Real cells combined into this marker (length 1 at close zoom). */
  members: ForecastCell[];
  row: number;
  col: number;
}

/**
 * Split/combine the real grid into markers for `zoom`.
 *
 *   - stride 1 (zoom >= 9): every real cell is its own marker — the
 *     original cells, positions unchanged.
 *   - stride >= 2 (wide zoom): each stride×stride block of the TRUE grid
 *     becomes ONE marker, anchored on the block's top-left real cell
 *     (grid-edge blocks clamp to whatever cells exist).
 *
 * Output is always derived from real cells — never invented screen points.
 */
export function planWeatherMarkers(
  cells: ForecastCell[],
  cols: number,
  zoom: number,
): WeatherMarkerGroup[] {
  const stride = zoomCellStride(zoom);
  const rows = Math.max(1, Math.ceil(cells.length / Math.max(1, cols)));
  if (stride <= 1) {
    return cells.map((c, i) => ({
      anchor: c,
      members: [c],
      row: Math.floor(i / cols),
      col: i % cols,
    }));
  }
  const groups: WeatherMarkerGroup[] = [];
  for (let r = 0; r < rows; r += stride) {
    for (let c = 0; c < cols; c += stride) {
      const members: ForecastCell[] = [];
      for (let rr = r; rr < Math.min(r + stride, rows); rr++) {
        for (let cc = c; cc < Math.min(c + stride, cols); cc++) {
          const idx = rr * cols + cc;
          if (idx < cells.length) members.push(cells[idx]);
        }
      }
      if (members.length === 0) continue;
      groups.push({ anchor: members[0], members, row: r, col: c });
    }
  }
  return groups;
}

// ── Combining (C1-4: severe wins, averages, highest-rain) ──

export interface WeatherHourValues {
  /** WMO code of the winning condition (null when no cell has one). */
  weatherCode: number | null;
  icon: WeatherIconKey;
  label: string;
  severe: boolean;
  /** °C — average across the group's cells (null when none report). */
  temperature: number | null;
  /** % — average relative humidity (null when none report). */
  humidity: number | null;
  /** % — highest rain chance in the group. */
  rainChance: number | null;
  /** mm — highest rain amount in the group (never understates rain). */
  rainMm: number | null;
}

const meanOf = (vals: number[]): number | null =>
  vals.length === 0 ? null : vals.reduce((a, b) => a + b, 0) / vals.length;

const maxOf = (vals: number[]): number | null => (vals.length === 0 ? null : Math.max(...vals));

/**
 * Combine one hour across the group's member cells:
 *   - condition: worst (most severe) code wins — severe always beats
 *     non-severe, so severe weather cannot disappear during grouping
 *   - temperature: average of the cells that report one
 *   - humidity: average of the cells that report one
 *   - rain chance: highest in the group
 *   - rain amount: highest in the group
 * Nulls are skipped; a group where nothing reports yields nulls (never 0).
 */
export function combineHours(
  memberHours: Array<WindPointHour | null | undefined>,
): WeatherHourValues {
  const hours = memberHours.filter((h): h is WindPointHour => !!h);
  const code = pickWorstCode(hours.map((h) => h.weatherCode ?? null));
  const temps = hours.map((h) => h.temperature).filter((v): v is number => v != null);
  const hums = hours.map((h) => h.humidity).filter((v): v is number => v != null);
  const chances = hours
    .map((h) => h.precipitationProbability)
    .filter((v): v is number => v != null);
  const rains = hours.map((h) => h.precipitation).filter((v): v is number => v != null);
  const meta = code != null ? weatherCodeMeta(code) : null;
  return {
    weatherCode: code,
    icon: meta?.icon ?? 'cloud',
    label: meta?.label ?? 'Forecast',
    severe: meta?.severe ?? false,
    temperature: meanOf(temps),
    humidity: meanOf(hums),
    rainChance: maxOf(chances),
    rainMm: maxOf(rains),
  };
}

// ── Details-card wording (C1-6) ──

/** "Now" for hour 0, else "+Nh (Fri 14:00)" — matches the Conditions slider. */
export function hourLabel(iso: string, hourIndex: number): string {
  if (hourIndex === 0) return 'Now';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return `+${hourIndex}h`;
  const t = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
  return `${t} (+${hourIndex}h)`;
}

/** "39.65°E, 4.04°S" — conventional lat/lon formatting for the source cell. */
export function fmtLatLon(lat: number, lon: number): string {
  const latS = `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? 'N' : 'S'}`;
  const lonS = `${Math.abs(lon).toFixed(2)}°${lon >= 0 ? 'E' : 'W'}`;
  return `${lonS}, ${latS}`;
}
